// src/pages/Login.jsx
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/AuthContext.jsx';

export default function Login() {
  const { login, verifyMfa } = useAuth();
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [mfaToken, setMfaToken] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  async function handlePasswordSubmit(e) {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      const result = await login(username, password);
      if (result.mfaRequired) {
        setMfaToken(result.mfaToken);
      } else {
        navigate('/');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  async function handleMfaSubmit(e) {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      await verifyMfa(mfaToken, code);
      navigate('/');
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-paper px-4">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <div className="text-2xl font-semibold text-ink">Sauti</div>
          <div className="text-sm text-ink/60 mt-1">Staff portal · Caritas of Hope</div>
        </div>

        <div className="bg-white rounded-xl border border-ink/10 shadow-sm p-6">
          {!mfaToken ? (
            <form onSubmit={handlePasswordSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-charcoal mb-1" htmlFor="username">Username</label>
                <input
                  id="username" type="text" required autoFocus value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="w-full rounded-md border border-ink/20 px-3 py-2 text-sm focus:border-accent focus:ring-1 focus:ring-accent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-charcoal mb-1" htmlFor="password">Password</label>
                <input
                  id="password" type="password" required value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full rounded-md border border-ink/20 px-3 py-2 text-sm focus:border-accent focus:ring-1 focus:ring-accent"
                />
              </div>
              {error && <p className="text-sm text-p1" role="alert">{error}</p>}
              <button
                type="submit" disabled={busy}
                className="w-full rounded-md bg-ink text-white text-sm font-medium py-2.5 hover:bg-ink-700 disabled:opacity-50 transition-colors"
              >
                {busy ? 'Signing in…' : 'Sign in'}
              </button>
            </form>
          ) : (
            <form onSubmit={handleMfaSubmit} className="space-y-4">
              <p className="text-sm text-ink/70">Enter the 6-digit code from your authenticator app.</p>
              <input
                type="text" inputMode="numeric" pattern="[0-9]*" maxLength={6} required autoFocus
                value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                className="w-full rounded-md border border-ink/20 px-3 py-2 text-sm tracking-[0.3em] text-center font-mono focus:border-accent focus:ring-1 focus:ring-accent"
                placeholder="000000"
              />
              {error && <p className="text-sm text-p1" role="alert">{error}</p>}
              <button
                type="submit" disabled={busy || code.length !== 6}
                className="w-full rounded-md bg-ink text-white text-sm font-medium py-2.5 hover:bg-ink-700 disabled:opacity-50 transition-colors"
              >
                {busy ? 'Verifying…' : 'Verify'}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
